<template>
    <div>
        <NavHeader></NavHeader>
        <div class="wrap info-box" :key="id">
            <template v-if="infoData.status == 5">
                <NoData :theme="theme" />
            </template>
            <template v-else>
                <DownloadHeader v-if="isInOutsideH5() && !isInRyH5()" />

                <ArticleHeader
                    :special-list="specialList"
                    :title="infoData.title"
                    :source="displaySource"
                    :publish-text="infoData.publishText"
                    :has-download-header="isInOutsideH5() && !isInRyH5()"
                    @go-special="goSpecialDetail"
                />

                <StockQuotes
                    ref="stockQuotesRef"
                    :quotes="quotes"
                    :theme="theme"
                    :mkt-color="mktColor"
                    :is-app="isApp"
                    :is-t-h-s-app="isTHSApp"
                    @attention="handleAttention"
                    @go-quote-detail="goQuoteDetail"
                />

                <ArticleBody :info-data="infoData" :js-bridge="jsBridge" :is-t-h-s-app="isTHSApp" />

                <RelatedNews :news-list="newsList" :theme="theme" @go-detail="goDetail" @go-quote-detail="goQuoteDetail" />

                <OpenAppFooter :appid="appid" :extinfo="extinfo" @launch="handleLaunch" @error="handleError" @open-app="openApp" />
            </template>

            <loading :msg="$t('loading')" propsShow :showLoading="showLoading" />
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance, nextTick } from 'vue'
import JSBridge from '@fs/jsbridge/dist/lib/jsBridge.js'
import { thsI18NJsBridge } from '@fs/jsbridge'
import NavHeader from '@/components/NavHeader/index.vue'
import Loading from '@/components/Loading.vue'
import NoData from './components/NoData.vue'
import DownloadHeader from './components/DownloadHeader.vue'
import ArticleHeader from './components/ArticleHeader.vue'
import StockQuotes from './components/StockQuotes.vue'
import ArticleBody from './components/ArticleBody.vue'
import RelatedNews from './components/RelatedNews.vue'
import OpenAppFooter from './components/OpenAppFooter.vue'
import { logo2x } from '@/config/cdnResoureUrl'

import { useArticle } from './composables/useArticle'
import { useRelatedData } from './composables/useRelatedData'
import { mktColor, fundMktsTypeMap } from './utils/constants'
import { isTHSApp, isIos, isWeiXin, isInOutsideH5, isInRyH5, isDingtalk } from '@/utils'
import { getLangType, getQueryString } from '@/utils/tools'
import { ddShare } from '@/utils/ddShare'
import { getWxShareSignature } from '@/apis/modules/share'
import wxshare from '@/mixins/wxshare'
import { Toast } from 'vant'

export default {
    name: 'InformationDetail',
    mixins: [wxshare],
    components: {
        NavHeader,
        Loading,
        NoData,
        DownloadHeader,
        ArticleHeader,
        StockQuotes,
        ArticleBody,
        RelatedNews,
        OpenAppFooter,
    },
    setup() {
        const instance = getCurrentInstance()
        const vm = instance.proxy

        const stockQuotesRef = ref(null)
        const jsBridge = ref(null)
        const isApp = ref(true)
        const appid = ref(process.env.VUE_APP_ID)
        const extinfo = ref('fosunhani://stock.goto/web?url=' + encodeURIComponent(window.location.href))
        const androidScheme = process.env.VUE_APP_SCHEME_ANDROID
        const iosScheme = process.env.VUE_APP_SCHEME_IOS
        const downloadTimer = ref(null)

        const {
            id,
            theme,
            infoData,
            specialList,
            showLoading,
            isSpecial,
            colorType,
            initParams,
            getSpecialRelated,
            getInformationDetail,
            setUserEvent,
        } = useArticle()

        const { quotes, newsList, getList, initPolling, stopPolling, updateQuoteFavorites, lockFavorite } = useRelatedData()

        const isFlashNewsType = (type) => {
            const raw = type ?? getQueryString('type', true) ?? ''
            if (!raw) return false

            let value = String(raw).trim()
            try {
                value = decodeURIComponent(value)
            } catch {
                // 已解码或非合法编码，沿用原值
            }
            return ['快讯', '快訊', 'Flash news'].includes(value)
        }

        const displaySource = computed(() => {
            if (isFlashNewsType()) {
                return vm.$t('flashNews')
            }
            return infoData.value?.source ?? ''
        })

        // Initialize parameters
        initParams(isTHSApp)

        // Initialize bridge early
        jsBridge.value = vm.jsBridge || vm.$jsBridge || window?.jsBridge || JSBridge || null

        // Actions
        const goSpecialDetail = specialId => {
            location.href = `${location.origin}/pages/special.html?t=${new Date().valueOf()}#/detail?id=${specialId}&colorType=${
                colorType.value
            }&langType=${getLangType()}`
        }

        const goDetail = newsId => {
            location.href = `${location.origin}/pages/informationDetail.html?t=${new Date().valueOf()}#/?id=${newsId}&colorType=${
                colorType.value
            }&langType=${getLangType()}`
        }

        const goFundDetail = item => {
            const domain = process.env.NODE_ENV === 'development' ? 'https://sit.mfosunhani.com' : location.origin
            location.href = `${domain}/wealth/fund.html?t=${new Date().valueOf()}#/detail?type=${fundMktsTypeMap[item.mkt]}&symbol=${item.code}`
        }

        const goQuoteDetail = item => {
            const bridge = jsBridge.value || vm.jsBridge || window?.jsBridge || JSBridge || null
            if (!bridge) {
                try {
                    if (thsI18NJsBridge?.isTHSI18NApp?.() && vm.$thsI18NJsBridge) {
                        const code = item.code
                        const url = `stockQuote?stockId=${code}`
                        vm.$thsI18NJsBridge.gotoCommonPage({ url })
                    }
                } catch (e) {}
                return
            }
            const code = item.code
            const isFund = Object.keys(fundMktsTypeMap).includes(item.mkt)
            if (isFund) {
                goFundDetail(item)
            } else {
                bridge.goPage(`stockQuote?stockId=${code}`)
            }
        }

        const handleAttention = async index => {
            if (!quotes.value || !quotes.value[index]) return
            const code = quotes.value[index].code
            if (!jsBridge.value) return
            try {
                const nextStatus = !quotes.value[index].favorStatus
                lockFavorite(code, 1000)
                quotes.value[index].favorStatus = nextStatus
                let result = null
                if (nextStatus) {
                    result = await jsBridge.value.addFavstock(code)
                } else {
                    result = await jsBridge.value.removeFavstock(code)
                }

                if (result !== 'success') {
                    let confirmed = false
                    for (let i = 0; i < 3; i++) {
                        await new Promise(resolve => setTimeout(resolve, 250))
                        try {
                            const favor = await jsBridge.value.checkFavstock(code)
                            if (!!favor?.isFavStock === nextStatus) {
                                confirmed = true
                                break
                            }
                        } catch (e) {}
                    }
                    if (!confirmed) {
                        quotes.value[index].favorStatus = !nextStatus
                        return
                    }
                }
            } catch (error) {
                Toast(error.desc || error.message)
            }
        }

        // Share Logic
        const getWxShareInfo = pageUrl => {
            return new Promise((resolve, reject) => {
                getWxShareSignature(pageUrl)
                    .then(r => {
                        if (r?.data?.result) {
                            const res = r.data.result || {}
                            resolve(res)
                        }
                    })
                    .catch(err => reject(err))
            })
        }

        const setShare = () => {
            nextTick(() => {
                const bridge = vm.jsBridge || vm.$jsBridge || window?.jsBridge || JSBridge || null
                jsBridge.value = bridge

                if (bridge && !isTHSApp()) {
                    // App environment
                    bridge.setClose(true)
                    let desc =
                        infoData.value.intro && infoData.value.intro != ''
                            ? infoData.value.intro
                            : document.querySelector('.information-content')?.innerText || ''
                    desc = desc.replace(/<s?img[^>]*>/gi, '')
                    if (desc.length > 200) desc = desc.slice(0, 200)

                    bridge.setButton('right1', { icon: 'share' }, () => {
                        const shareUrl = `${window.location.href}&colorType=${colorType.value}&langType=${getLangType()}${
                            isSpecial.value ? '&textType=special' : ''
                        }`
                        bridge.share({
                            pageUrl: shareUrl,
                            title: infoData.value.title || desc,
                            desc: desc,
                            thumbUrl: infoData.value?.picUrls?.[0],
                        })
                    })
                    isApp.value = true
                } else if (isWeiXin() || isDingtalk()) {
                    // WeChat / DingTalk
                    isApp.value = false
                    let desc =
                        infoData.value.intro && infoData.value.intro != ''
                            ? infoData.value.intro
                            : document.querySelector('.information-content')?.innerText || ''
                    desc = desc.replace(/<s?img[^>]*>/gi, '')
                    if (desc.length > 200) desc = desc.slice(0, 200)

                    ddShare({
                        title: infoData.value.title || desc,
                        content: desc,
                    })

                    if (isWeiXin()) {
                        const pageUrl = encodeURIComponent(location.href.split('#')[0])
                        getWxShareInfo(pageUrl).then(res => {
                            // Call mixin method via vm
                            vm.timestamp = res?.timestamp
                            vm.nonceStr = res?.nonce_str
                            vm.signature = res?.signature
                            vm.jsapiTicket = res?.jsapiTicket
                            vm.appId = res?.app_id
                            vm.wxShare({
                                title: infoData.value.title || desc,
                                desc: desc,
                                type: '',
                                imgUrl: '',
                                link: location.href,
                            })
                        })
                    }
                } else {
                    isApp.value = false
                }
            })
        }

        // App Open/Download Logic
        const download_app = () => {
            window.location.href = 'https://h5.fotechwealth.com/m/'
        }

        const openApp = () => {
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState == 'hidden') {
                    if (downloadTimer.value) clearTimeout(downloadTimer.value)
                }
            })
            let scheme = isIos() ? iosScheme : androidScheme
            window.location.href = `${scheme}/web?url=${encodeURIComponent(window.location.href)}`
            downloadTimer.value = setTimeout(() => {
                download_app()
            }, 3000)
        }

        const handleLaunch = e => {
            console.log(e, e.detail, 'Launch')
        }

        const handleError = e => {
            console.log(e, 'handleError')
            if (e.detail.errMsg == 'launch:fail') {
                download_app()
            }
        }

        // Lifecycle
        onMounted(async () => {
            vm.$store.commit('app/updateNavTitle', vm.$t('informationTitle'))

            getSpecialRelated()
            setUserEvent()

            // Fetch Detail
            const result = await getInformationDetail(vm.$t.bind(vm))
            if (result) {
                setShare()

                if (result.stocks && result.stocks.length > 0) {
                    // Fetch List
                    getList({
                        stocks: result.stocks,
                        id: id.value,
                        theme: theme.value,
                        colorType: colorType.value,
                        jsBridge: jsBridge.value,
                    })

                    // Init Polling
                    initPolling({
                        id: id.value,
                        stocks: result.stocks,
                        theme: theme.value,
                        colorType: colorType.value,
                        jsBridge: jsBridge.value,
                        onTick: () => {
                            if (quotes.value && quotes.value.length >= 2) {
                                stockQuotesRef.value?.nextCarousel()
                            }
                        },
                    })
                }
            }
        })

        onBeforeUnmount(() => {
            stopPolling()
            if (downloadTimer.value) clearTimeout(downloadTimer.value)
        })

        return {
            id,
            theme,
            infoData,
            displaySource,
            specialList,
            showLoading,
            quotes,
            newsList,
            stockQuotesRef,
            jsBridge,
            isApp,
            appid,
            extinfo,
            mktColor,
            isTHSApp,
            isInOutsideH5,
            isInRyH5,
            isWeiXin,
            goSpecialDetail,
            goDetail,
            goQuoteDetail,
            handleAttention,
            handleLaunch,
            handleError,
            openApp,
        }
    },
}
</script>

<style lang="less">
// Global styles that were present in the original file
@import url('./css/mixins.less');

.news-images-overlay .van-overlay {
    background-color: rgba(0, 0, 0, 1) !important;
}

// Reuse some basic layout styles
.info-box {
    min-height: 100vh;
    height: auto;
    overflow: visible;
    #news_pages_background();
}

.wrap {
    padding: 16px 16px 60px;
    font-size: 12px;
}
</style>
<style lang="less">
@import url('./css/mixins.less');

.news-images-overlay .van-overlay {
    background-color: rgba(0, 0, 0, 1) !important;
}

.information-content table {
    width: 100% !important;
}

.information-content {
    p:not(:last-child) {
        margin-bottom: @p_margin_bottom;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 180%;
    }
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        margin-bottom: @p_margin_bottom;
        font-size: 18px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;

        #news_title();
    }
    blockquote {
        margin-bottom: @p_margin_bottom;
    }
    img {
        width: 100%;
    }
    ol {
        list-style: decimal !important;
    }
    ul {
        list-style: disc !important;
    }
    ul,
    ol {
        padding-left: 1.5em;
        margin-left: 0;
        list-style-position: outside;
        margin-bottom: @p_margin_bottom;
    }
    li::marker {
        position: relative;
        left: 0.5em;
    }
    @media (max-width: 414px) {
        ul,
        ol {
            padding-left: 1.35em;
        }
        li::marker {
            left: 0.45em;
        }
    }
    @media (max-width: 375px) {
        ul,
        ol {
            padding-left: 1.25em;
        }
        li::marker {
            left: 0.4em;
        }
    }
}

.hn-scroll-area {
    width: 100%;
    overflow-x: auto;
    overflow-y: visible; // 允许纵向滚动
    touch-action: pan-x pan-y !important; // 允许横向和纵向滚动
    overscroll-behavior-x: contain; // 防止过度滚动
    margin: 12px 0;

    // 现代移动端滚动支持
    scroll-behavior: smooth;

    // 确保在移动端可以滚动
    position: relative;
    z-index: 1;

    // 隐藏横向滚动条但保持滚动功能
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */

    &::-webkit-scrollbar {
        display: none; /* Chrome, Safari, Opera */
    }

    // 移动端触摸优化
    @media (max-width: 768px) {
        -webkit-overflow-scrolling: touch; // 仅在移动端使用
        touch-action: pan-x pan-y !important; // 允许横向和纵向滚动
    }
}

table {
    width: max-content;
    min-width: 100%;
    border-collapse: collapse;
    table-layout: auto;
    white-space: nowrap;
}

th,
td {
    padding: 8px;
    text-align: left;
    border: 1px solid #ddd;
    white-space: nowrap;
    min-width: 80px; // 设置最小宽度确保内容可读
}

th {
    background-color: #f2f2f2;
}
</style>
<style lang="less" scoped>
@import url('~@/assets/css/mixins.less');
@import url('./css/mixins.less');

.info-box {
    min-height: 100vh;
    height: auto;
    overflow: visible;
    #news_pages_background();
}

.wrap {
    padding: 16px 16px 60px;
    // #font_theme();
    font-size: 12px;
}

.information-title {
    margin-bottom: 16px;
    font-weight: 600;
    font-size: 21px;
    line-height: 1.5;
    text-align: left;
    word-wrap: break-word;
    text-align: justify;
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    color: #181818;
    margin-bottom: @p_margin_bottom;
    font-weight: 600;
    #news_title();
}

.information-desc {
    padding: 0 0 12px;
    color: #9c9c9c;
    text-align: justify;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    font-style: normal;
    #news_desc();

    span {
        margin-left: 8px;
    }
}

.information-content {
    margin: 16px 0 12px;
    font-size: 16px;
    line-height: 30px;
    line-height: 1.7;
    text-align: justify; // Modified: Justify alignment
    word-wrap: break-word;
    word-break: break-all; // Added: Force line break to prevent overflow
    #news_content();

    table {
        width: 100% !important;
    }

    img {
        width: 100%;
    }
}

.news-title {
    padding-top: 20px;
    padding-bottom: 16px;
    font-weight: 600;
    font-size: 16px;
    #font_h1();
    font-size: 18px;
    #related_news();
}

.stock-list-item-sticky {
    position: -webkit-sticky;
    position: sticky;
    top: var(--nav-header-height);
    z-index: 98;
    #news_pages_background();
}
.stock-list {
    padding: 8px 0;
    position: relative;
    overflow: hidden;
    border-radius: 8px;
    // 设置固定高度，显示 itemsPerPage 个股票项
    // 高度需要根据实际股票项高度动态计算，这里设置一个合理的默认值
    // height: 180px;
    // 确保容器有明确的定位上下文
    display: block;
}

.stock-carousel-wrapper {
    position: relative;
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: transform;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    transform-style: preserve-3d;
    // 确保 wrapper 可以正确滚动
    display: block;
    // 确保 transform 能正确应用
    transform-origin: top center;
}

.stock-box {
    position: relative;
}

.stock-sticky {
    position: fixed;
    top: var(--nav-header-height);
    left: 0;
    right: 0;
    z-index: 100;
    transform: translateY(0);
    transition: transform 0.2s ease, opacity 0.2s ease;
    touch-action: pan-y;
}

.stock-sticky-inner {
    padding: 0 16px;
    .stock-list-item {
        min-height: 54px;
        #foreground();
    }
}

.sticky-sentinel {
    width: 100%;
    height: 1px;
}

.information-icon-love {
    display: block;
    width: 20px;
    height: 20px;
    background: url('~@/assets/images/information/icon_love.png') no-repeat center;
    background-size: 100%;

    &.black {
        background: url('~@/assets/images/information/icon_love_black.png') no-repeat center;
        background-size: 100%;
    }

    &.active {
        background: url('~@/assets/images/information/icon_love_active.png') no-repeat center;
        background-size: 100%;
    }
}

.intro-box {
    display: flex;
    padding: 12px;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    border-radius: 12px;
    background: #f7f7f7;
    margin-top: 16px;
    #intro_background();
    .intro {
        color: #181818;
        text-align: justify;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 160%;
        #intro_content();
    }
}

.news-list {
    .list-item {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        padding: 12px 0 8px;
        // border-bottom:1px solid #efefef;
        #box_shadow();

        .text {
            flex: 1;
            margin-right: 16px;

            .one {
                display: -webkit-box;
                max-height: 48px;
                overflow: hidden;
                font-size: 16px;
                line-height: 24px;
                text-overflow: ellipsis;
                word-break: break-all;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 2;
                #font_h1();
            }

            .two {
                .item {
                    display: inline-block;
                    margin: 4px 8px 0 0;
                    padding: 4px 8px;
                    background: #f9f9f9;
                    border-radius: 50px;
                    // #fore_ground();
                }
            }

            .three {
                margin-top: 10px;
                // color:#9C9C9C;
                font-size: 11px;
                #font_h3();

                span {
                    margin-left: 8px;
                }
            }
        }

        .img {
            flex: 0 120px;
            height: 80px;
            overflow: hidden;
            border-radius: 4px;

            img {
                width: 120px;
            }
        }
    }
}

.share-header {
    position: fixed;
    top: var(--nav-header-height);
    left: 0;
    z-index: 99;
    // #fore_ground();
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 80px;
    padding: 16px;
    background-color: #fff;

    & + .information-header {
        padding-top: 80px;
    }

    .logo {
        text-align: center;

        img {
            width: 110px;
        }

        p {
            margin-top: 4px;
            margin-left: 8px;
            font-size: 12px;
            letter-spacing: 0.35em;
            // #font_theme();
        }
    }

    .qr {
        width: 60px;
        height: 60px;
    }

    .dowload {
        width: 52px;
        height: 28px;
        color: #fff;
        font-size: 14px;
        background-color: #ff6907;
        border: 1px solid;
        border-radius: 31px;
    }
}

.nodata {
    position: absolute;
    top: 50%;
    left: 50%;
    text-align: center;
    transform: translate(-50%, -50%);

    p {
        color: #999;
        font-size: 14px;
        // #font_theme();
    }
}

.special-list {
    margin-bottom: @p_margin_bottom;
}

.special-list-item {
    position: relative;
    #font_h1();
    // border-bottom: 1px solid #efefef;
    #box_shadow();

    .icon-special {
        position: absolute;
        top: 50%;
        left: 0;
        width: 14px;
        height: 14px;
        margin-top: -8px;
        background: url('~@/assets/images/information/special.jpg') no-repeat center;
        background-size: 100%;
    }

    p {
        padding: 8px 0 8px 20px;
        font-size: 14px;
    }
}

.footer-box {
    position: fixed;
    bottom: 50px;
    left: 50%;
    transform: translateX(-50%);

    .footer-btn {
        padding: 6px 12px;
        color: #fff;
        font-size: 14px;
        background-color: #ff6907;
        border: 1px solid;
        border-radius: 31px;
    }
}
</style>
